import { Logger } from '@frontend/logger';
import React, { createContext, useContext, useEffect, useState } from 'react';
import ScanbotSDK from 'scanbot-web-sdk';

const ScanbotSDKProviderContext = createContext<{
    validLicense: boolean;
    scanbotSDK: ScanbotSDK | null;
    sdkLoading: boolean;
} | null>(null);

// Production license key DO NOT CHANGE (unless very sure) | valid until November 26, 2026 (+2-months of grace period)
const PROD_LICENSE_KEY =
    'hQWIF97ZSFabscRyzMcTk+Uo4AOI4g' +
    'zTVRxZWfxw/KOcKJnEipMCGm+IqVEj' +
    'uFpwiAJ02eGBXJlyUt1cQwBppAKSA4' +
    'f0uzPblpVuOa5bopPUcWhf3oTFjsux' +
    '1C3IOdLTYGg412ojxtOiJKICajIpR2' +
    '00LvxjhYmZEhfUhpsesFjewApZRqFc' +
    'bqIexV7KXt8w/6taueJOTBGput6hBz' +
    'ieQqZGG9eRV5OM6v+d/Cdk0KCxaOS+' +
    'x+7ef0/3oPH7OMjBhiv8vQcmWjhuja' +
    'Sm8mhV6EbVcCEKiaYm13wYn4xy/8Ea' +
    '/M7wxp0gV3IcgbNLsbU1sodwgBQBeg' +
    '7jSRBAQr/XrQ==\nU2NhbmJvdFNESw' +
    'oqdGVsbG9wb3J0LmlvfCp0ZWxsb3Bv' +
    'cnQubmV0fCp0ZWxsb3BvcnQuY29tfG' +
    'xvY2FsaG9zdAoxODAxMDA3OTk5CjEx' +
    'NTU2NzgKOA==\n';

// Testing license key (update when testing new license features on test or dev envs)
const TESTING_LICENSE_KEY =
    'hQWIF97ZSFabscRyzMcTk+Uo4AOI4g' +
    'zTVRxZWfxw/KOcKJnEipMCGm+IqVEj' +
    'uFpwiAJ02eGBXJlyUt1cQwBppAKSA4' +
    'f0uzPblpVuOa5bopPUcWhf3oTFjsux' +
    '1C3IOdLTYGg412ojxtOiJKICajIpR2' +
    '00LvxjhYmZEhfUhpsesFjewApZRqFc' +
    'bqIexV7KXt8w/6taueJOTBGput6hBz' +
    'ieQqZGG9eRV5OM6v+d/Cdk0KCxaOS+' +
    'x+7ef0/3oPH7OMjBhiv8vQcmWjhuja' +
    'Sm8mhV6EbVcCEKiaYm13wYn4xy/8Ea' +
    '/M7wxp0gV3IcgbNLsbU1sodwgBQBeg' +
    '7jSRBAQr/XrQ==\nU2NhbmJvdFNESw' +
    'oqdGVsbG9wb3J0LmlvfCp0ZWxsb3Bv' +
    'cnQubmV0fCp0ZWxsb3BvcnQuY29tfG' +
    'xvY2FsaG9zdAoxODAxMDA3OTk5CjEx' +
    'NTU2NzgKOA==\n';

const env = process.env['NX_PUBLIC_BUILD_ENV'];
const LICENSE_KEY = env && ['test', 'development'].includes(env) ? TESTING_LICENSE_KEY : PROD_LICENSE_KEY;

export const ScanbotSDKProvider = (props: { children: React.ReactNode }) => {
    const [validLicense, setValidLicense] = useState<boolean>(true);
    const [scanbotSDK, setScanbotSDK] = useState<ScanbotSDK | null>(null);
    const [sdkLoading, setSdkLoading] = useState<boolean>(true);
    useEffect(() => {
        try {
            ScanbotSDK.initialize({ licenseKey: LICENSE_KEY, enginePath: 'public/wasm' }).then((sdk) => {
                setScanbotSDK(sdk);
                setSdkLoading(false);
            });
        } catch (e) {
            Logger.error(e);
            setSdkLoading(false);
        }
    }, []);

    useEffect(() => {
        if (scanbotSDK) {
            try {
                scanbotSDK.getLicenseInfo().then((licenseInfo) => {
                    const isValid = licenseInfo.isValid;
                    if (!isValid) {
                        Logger.error('License is not valid');
                    }
                    setValidLicense(isValid);
                });
            } catch (e) {
                Logger.error(e);
            }
        }
    }, [scanbotSDK]);

    return <ScanbotSDKProviderContext.Provider value={{ scanbotSDK, sdkLoading, validLicense }}>{props.children}</ScanbotSDKProviderContext.Provider>;
};

export const useScanbotSDKProvider = () => {
    const context = useContext(ScanbotSDKProviderContext);
    if (!context) {
        throw new Error('useScanbotSDKProvider must be used within an ScanbotSDKProvider');
    }
    return context;
};
